import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Box, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useStoreState } from "easy-peasy";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import UserService from "../../../../../services/userService";
import { InputAdornment } from "@material-ui/core";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AdobeScript from "../../../../../services/adobeScript";

const useStyles = makeStyles({
  textField: (props) => ({
    width: '100%',
    "& label.Mui-focused": { color: props.brandColor },
    "& .MuiInputBase-root": {
      backgroundColor: "#fff",
      width: 400,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#E0E3E7" },
      "&:hover fieldset": { borderColor: "#B2BAC2" },
      "&.Mui-focused fieldset": { borderColor: props.brandColor },
    },
    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: 4,
      backgroundColor: "#fff",

      "& fieldset": {
        borderColor: "#D9DDE4",
      },

      "&:hover fieldset": {
        borderColor: "#D9DDE4",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#4B86C5",
      },
    },

    "& .MuiOutlinedInput-input": {
      fontSize: 14,
      padding: "8px 0",
      color: "#555",

      "&::placeholder": {
        color: "#B9C0CA",
        opacity: 1,
      },
    },

    "& .MuiInputAdornment-root": {
      marginRight: 4,
    },
  }),
});

const UserCreation = forwardRef(function UserCreation({
  label = "Create Username",
  min = 8,
  max = 50,
  onValidityChange,
  applicantId
}, ref) {
  const userService = UserService();
  const adobe = new AdobeScript();

  const brandSettings = useStoreState((s) => s.siteSettingsModel.brandSettings);
  const brandColor = brandSettings.FooterBgColor;
  const classes = useStyles({ brandColor });
  const [username, setUsername] = useState("");

  const usernameLength = username.replace(/\s/g, "").length;
  const lenOk = usernameLength >= min && usernameLength <= max;
  const allowedOnly = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/.test(username);
  const [availability, setAvailability] = useState("idle");
  const isFormatValid = lenOk && allowedOnly;

  useEffect(() => {
    if (typeof onValidityChange === "function") {
      onValidityChange(isFormatValid);
    }
  }, [isFormatValid, onValidityChange]);

  useImperativeHandle(ref, () => ({
    checkAvailability: async () => {
      const u = username.trim();

      if (!isFormatValid || !u) {
        setAvailability("idle");
        return false;
      }

      setAvailability("checking");

      try {
        const response = await userService.checkUserAvailability(u, applicantId);

        if (response && response.IsAvailable) {
          setAvailability("available");
          userService.setUser({ username: u });
          return true;
        }

        setAvailability("taken");
        return false;
      } catch (err) {
        console.error(err);
        setAvailability("error");
        return false;
      }
    },
  }), [applicantId, isFormatValid, userService, username]);

  return (
    <Box  style={{ 
        maxWidth: 480,  
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}>
      <TextField
        variant="outlined"
        placeholder={label}
        value={username}
        onFocus={() => adobe.event("createUsername")}
        onChange={(e) => {
          setUsername(e.target.value);
          setAvailability("idle");
        }}
        inputProps={{
          maxLength: max,
          "aria-label": label,
          autoComplete: "off",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonOutlineIcon
                style={{
                  fontSize: 16,
                  color: "#B9C0CA",
                }}
              />
            </InputAdornment>
          ),
        }}
        className={classes.textField}
      />

      <Box 
        mt={1} 
        style={{
          fontSize: 12,
          marginTop: 10,
        }}
      >
        Your username must
        <Rule ok={lenOk}>
          8 - {max} characters long
        </Rule>
        <Rule ok={allowedOnly}>
          Alphanumeric
        </Rule>
        <Rule ok={availability === "available"}>
          {
            availability === "checking" ? "Checking availability" :
            availability === "taken" ? "Username already in use" :
            availability === "error" ? "Error checking availability" : 
            availability === "available" ? "Username available" :
            isFormatValid ? "Click Next to check availability" : "Invalid username"
          }
        </Rule>
      </Box>
    </Box>
  );
});

function Rule({ ok, children }) {
  const color = ok ? "#00B232" : "#FF0000";
  return (
    <Box display="flex" alignItems="center" style={{ marginTop: 1 }}>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          width: 16,
          justifyContent: "center",
          marginRight: 8,
          color,
          fontWeight: 700
        }}
      >
         {ok ? (
        <CheckIcon style={{ color, fontSize: 10, marginBottom: 1 }} />
      ) : (
        <CloseIcon style={{ color, fontSize: 10, marginBottom: 1 }} />
      )}
      </span>
      <Typography variant="body2" 
      style={{ 
          color,
          fontSize: "12px",  
          lineHeight: "20px",
          fontWeight: 100,
          margin: 0,
          }}>
        {children}
      </Typography>
    </Box>
  );
}

export default UserCreation;
